const __DEV__ = false;

import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useTheme, jsx } from '@emotion/react';
import { getStyles, sanitizeProps } from '../../styles/theme/index.js';
import { S } from './styles.js';
import mergeDefaultProps from '../../utils/merge-default-props.js';
import useId from '../../hooks/use-auto-id/use-auto-id.js';
import makeId from '../../utils/make-id.js';
import useForkRef from '../../hooks/use-fork-ref/use-fork-ref.js';
import useIsFocusVisible from '../../hooks/use-is-focus-visible/use-is-focus-visible.js';
import usePrevious from '../../hooks/use-previous/use-previous.js';
import useImperativeState from '../../hooks/use-imperative-state/use-imperative-state.js';
import useEventCallback from '../../hooks/use-event-callback/use-event-callback.js';
import { composeEventHandlers } from '../../utils/compose-event-handlers.js';
import { Popover } from '../Popover/popover.js';

var _excluded = ["children", "content", "dataTestId", "describeChild", "disabled", "disableFocusListener", "disableHoverListener", "disableInteractive", "disableTouchListener", "enterDelay", "enterTouchDelay", "id", "isDesktopOnly", "isVisible", "leaveDelay", "leaveTouchDelay", "modifiers", "onClose", "onOpen", "placement", "popperOptions"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var TOOLTIP_OFFSET = 10;
var preserveRef = function preserveRef(ref, node) {
  if (ref && typeof ref !== 'string') {
    if (typeof ref === 'function') {
      ref(node);
    } else if ({}.hasOwnProperty.call(ref, 'current')) {
      ref.current = node;
    }
  }
};
var defaultProps = {
  describeChild: false,
  disabled: false,
  disableFocusListener: false,
  disableHoverListener: false,
  disableInteractive: false,
  disableTouchListener: false,
  enterDelay: 100,
  enterTouchDelay: 700,
  isDesktopOnly: false,
  leaveDelay: 0,
  leaveTouchDelay: 1500,
  modifiers: [],
  placement: 'top',
  popperOptions: {}
};
var TooltipInternal = function TooltipInternal(props, ref) {
  var propsInternal = mergeDefaultProps(props, defaultProps);
  var children = propsInternal.children,
      content = propsInternal.content,
      _propsInternal$dataTe = propsInternal.dataTestId,
      dataTestId = _propsInternal$dataTe === void 0 ? 'tooltip' : _propsInternal$dataTe,
      describeChild = propsInternal.describeChild,
      disabled = propsInternal.disabled,
      disableFocusListener = propsInternal.disableFocusListener,
      disableHoverListener = propsInternal.disableHoverListener,
      disableInteractive = propsInternal.disableInteractive,
      disableTouchListener = propsInternal.disableTouchListener,
      enterDelay = propsInternal.enterDelay,
      enterTouchDelay = propsInternal.enterTouchDelay,
      propsId = propsInternal.id;
      propsInternal.isDesktopOnly;
      var isVisible = propsInternal.isVisible,
      leaveDelay = propsInternal.leaveDelay,
      leaveTouchDelay = propsInternal.leaveTouchDelay,
      modifiers = propsInternal.modifiers,
      onClose = propsInternal.onClose,
      onOpen = propsInternal.onOpen,
      placement = propsInternal.placement,
      popperOptions = propsInternal.popperOptions,
      rest = _objectWithoutProperties(propsInternal, _excluded);
  var providedId = useId(propsId);
  var id = propsId !== null && propsId !== void 0 ? propsId : makeId('tooltip', providedId);
  var ruiTheme = useTheme();
  var ignoreNonTouchEvents = React.useRef(false);
  var closeTimer = React.useRef();
  var enterTimer = React.useRef();
  var leaveTimer = React.useRef();
  var touchTimer = React.useRef();
  var popRef = React.useRef(null);
  var ownRef = useForkRef(popRef, ref);
  var _useIsFocusVisible = useIsFocusVisible(),
      isFocusVisibleRef = _useIsFocusVisible.isFocusVisibleRef,
      handleBlurVisible = _useIsFocusVisible.onBlur,
      handleFocusVisible = _useIsFocusVisible.onFocus,
      focusVisibleRef = _useIsFocusVisible.ref;
  var childrenRef = useForkRef(focusVisibleRef, children.ref);
  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      setChildIsFocusVisible = _React$useState2[1];
  var _React$useState3 = React.useState(null),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      arrowRef = _React$useState4[0],
      setArrow = _React$useState4[1];
  var _React$useState5 = React.useState(null),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      anchorEl = _React$useState6[0],
      setAnchorEl = _React$useState6[1];
  var _React$useState7 = React.useState(false),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      isMounted = _React$useState8[0],
      setIsMounted = _React$useState8[1];
  var visible = isVisible !== null && isVisible !== void 0 ? isVisible : Boolean(anchorEl);
  var previousVisible = usePrevious(visible);
  var mutableContainer = useImperativeState(function () {
    return {
      container: ownRef
    };
  });
  React.useImperativeHandle(ref, function () {
    return mutableContainer.container;
  });
  React.useEffect(function () {
    if (previousVisible !== visible) {
      setIsMounted(Boolean(visible));
    }
  }, [previousVisible, visible]);
  var popoverOptions = React.useMemo(function () {
    var _popperOptions$modifi;
    var tooltipModifiers = [{
      name: 'offset',
      options: {
        offset: [0, TOOLTIP_OFFSET]
      }
    }, {
      name: 'arrow',
      enabled: Boolean(arrowRef),
      options: {
        element: arrowRef,
        padding: 0
      }
    }];
    if (modifiers !== null && modifiers !== void 0 && modifiers.length) {
      tooltipModifiers = tooltipModifiers.concat(modifiers);
    }
    if (popperOptions !== null && popperOptions !== void 0 && (_popperOptions$modifi = popperOptions.modifiers) !== null && _popperOptions$modifi !== void 0 && _popperOptions$modifi.length) {
      tooltipModifiers = tooltipModifiers.concat(popperOptions.modifiers);
    }
    return _objectSpread(_objectSpread({}, popperOptions), {}, {
      modifiers: tooltipModifiers
    });
  }, [arrowRef, modifiers, popperOptions]);
  var detectTouchStart = React.useCallback(function (event) {
    ignoreNonTouchEvents.current = true;
    var childrenProps = children.props;
    if (childrenProps.onTouchStart) {
      childrenProps.onTouchStart(event);
    }
  }, [children.props]);
  var prevUserSelect = React.useRef();
  var stopTouchInteraction = React.useCallback(function () {
    if (prevUserSelect.current !== undefined) {
      document.body.style.userSelect = prevUserSelect.current;
      prevUserSelect.current = undefined;
    }
    clearTimeout(touchTimer.current);
  }, []);
  React.useEffect(function () {
    return function () {
      clearTimeout(closeTimer.current);
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
      stopTouchInteraction();
    };
  }, [stopTouchInteraction]);
  var handleOpen = React.useCallback(function (event) {
    if (disabled) return;
    setAnchorEl(mutableContainer.ref);
    if (onOpen && !visible) {
      onOpen(event);
    }
  }, [disabled, mutableContainer.ref, onOpen, visible]);
  var handleClose = useEventCallback(function (event) {
    if (disabled) return;
    setAnchorEl(null);
    if (onClose && visible) {
      onClose(event);
    }
    clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(function () {
      ignoreNonTouchEvents.current = false;
    }, ruiTheme.transitions.duration.shortest);
  });
  var handleEnter = React.useCallback(function (event) {
    if (ignoreNonTouchEvents.current && event.type !== 'touchstart') {
      return;
    }
    if (mutableContainer.ref) {
      mutableContainer.ref.removeAttribute('title');
    }
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    if (enterDelay) {
      enterTimer.current = window.setTimeout(function () {
        setAnchorEl(mutableContainer.ref);
      }, enterDelay);
      return;
    }
    handleOpen(event);
  }, [enterDelay, handleOpen, mutableContainer.ref]);
  var handleFocus = React.useCallback(function (event) {
    if (!mutableContainer.ref) {
      setAnchorEl(mutableContainer.ref);
    }
    handleFocusVisible(event);
    if (isFocusVisibleRef.current === true) {
      setChildIsFocusVisible(true);
      handleEnter(event);
    }
  }, [handleEnter, handleFocusVisible, isFocusVisibleRef, mutableContainer.ref]);
  var handleLeave = React.useCallback(function (event) {
    var overrideTime = leaveDelay || (!disableInteractive ? 100 : 0);
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(function () {
      handleClose(event);
    }, overrideTime);
  }, [disableInteractive, handleClose, leaveDelay]);
  var handleTouchStart = React.useCallback(function (event) {
    detectTouchStart(event);
    clearTimeout(leaveTimer.current);
    clearTimeout(closeTimer.current);
    stopTouchInteraction();
    prevUserSelect.current = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    touchTimer.current = window.setTimeout(function () {
      var _prevUserSelect$curre;
      document.body.style.userSelect = (_prevUserSelect$curre = prevUserSelect.current) !== null && _prevUserSelect$curre !== void 0 ? _prevUserSelect$curre : '';
      handleEnter(event);
    }, enterTouchDelay);
  }, [detectTouchStart, enterTouchDelay, handleEnter, stopTouchInteraction]);
  var handleTouchEnd = React.useCallback(function (event) {
    if (children.props.onTouchEnd) {
      children.props.onTouchEnd(event);
    }
    clearTimeout(touchTimer.current);
    clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(function () {
      handleClose(event);
    }, leaveTouchDelay);
  }, [children.props, handleClose, leaveTouchDelay]);
  var handleMouseOver = handleEnter;
  var handleMouseLeave = handleLeave;
  var handleBlur = React.useCallback(function (event) {
    handleBlurVisible();
    if (isFocusVisibleRef.current === false) {
      setChildIsFocusVisible(false);
      handleLeave(event);
    }
  }, [handleBlurVisible, handleLeave, isFocusVisibleRef]);
  var propsAndState = _objectSpread({
    visible: visible
  }, propsInternal);
  var newChildrenProps = React.useMemo(function () {
    return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, describeChild && visible && _defineProperty({}, 'aria-describedby', id)), !describeChild && _objectSpread(_objectSpread({}, typeof content === 'string' ? _defineProperty({}, 'aria-label', content) : {}), visible && typeof content !== 'string' ? _defineProperty({}, 'aria-labelledby', id) : {})), {}, {
      tabIndex: 0
    }, children.props), {}, {
      ref: function ref(node) {
        mutableContainer.ref = node;
        preserveRef(childrenRef, node);
      }
    }, !disableTouchListener && {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd
    }), !disableHoverListener && {
      onMouseOver: composeEventHandlers(children.props.onMouseOver, handleMouseOver),
      onMouseLeave: composeEventHandlers(children.props.onMouseLeave, handleMouseLeave)
    }), !disableFocusListener && {
      onFocus: composeEventHandlers(children.props.onFocus, handleFocus),
      onBlur: composeEventHandlers(children.props.onBlur, handleBlur)
    });
  }, [children, childrenRef, content, describeChild, disableFocusListener, disableHoverListener, disableTouchListener, handleBlur, handleFocus, handleMouseOver, handleMouseLeave, handleTouchEnd, handleTouchStart, id, mutableContainer, visible]);
  var popoverInteractiveProps = React.useMemo(function () {
    return !disableInteractive ? _objectSpread(_objectSpread({}, !disableHoverListener && {
      onMouseOver: handleMouseOver,
      onMouseLeave: handleMouseLeave
    }), !disableFocusListener && {
      onFocus: handleFocus,
      onBlur: handleBlur
    }) : {};
  }, [disableFocusListener, disableHoverListener, disableInteractive, handleBlur, handleFocus, handleMouseOver, handleMouseLeave]);
  return jsx(React.Fragment, null, children ? React.cloneElement(children, newChildrenProps) : null, jsx(Popover, _extends({
    ref: mutableContainer.container,
    anchorEl: mutableContainer.ref,
    css: function css(theme) {
      return [S.container(theme, propsAndState), getStyles('container', theme, propsAndState)];
    },
    "data-testid": dataTestId,
    id: id,
    isOpen: visible,
    onClose: function onClose() {
      return setAnchorEl(null);
    },
    placement: placement,
    popperOptions: popoverOptions
  }, popoverInteractiveProps, sanitizeProps(rest, [], ['customCss'])), jsx("div", {
    css: function css(theme) {
      return [S.tooltip(theme, propsAndState), getStyles('tooltip', theme, propsAndState)];
    },
    "data-state": isMounted ? 'visible' : 'hidden',
    role: "complementary"
  }, jsx("div", {
    css: function css(theme) {
      return [S.content(theme, propsAndState), getStyles('content', theme, propsAndState)];
    },
    "data-state": isMounted ? 'visible' : 'hidden'
  }, content), jsx("div", {
    ref: setArrow,
    css: function css(theme) {
      return [S.arrow(theme, propsAndState), getStyles('arrow', theme, propsAndState)];
    },
    "data-popper-arrow": true
  }))));
};
TooltipInternal.displayName = 'TooltipInternal';
var Tooltip = Object.assign( React.forwardRef(TooltipInternal), {
  displayName: 'Tooltip'
});

export { Tooltip };
//# sourceMappingURL=tooltip.js.map
